import type { Family, Monster, Move, StatKey } from './types'
import { generateLearnableMove, generateMonster } from './generator'
import { evolutionsOf, STAGE_INFO } from './progression'
import { STATUS_INFO } from './statuses'

// --- affichage des coups (icône + tags) — partagé Battle/MonsterDetail/2v2 ----
const STAT_ABBR: Record<StatKey, string> = { attack: 'ATK', defense: 'DEF', speed: 'SPD', accuracy: 'PRÉC', evasion: 'ESQ' }

export function moveIcon(m: Move): string {
  const cat = m.category ?? 'damage'
  if (cat === 'buff') return '▲'
  if (cat === 'debuff') return '▼'
  if (cat === 'heal') return '✚'
  if (cat === 'status') return '✧'
  return m.kind === 'special' ? '✦' : '⚔'
}

/** Étiquettes décrivant les effets d'un coup (pour l'UI). */
export function moveTags(m: Move): string[] {
  const t: string[] = []
  if (m.statChanges) for (const s of m.statChanges) t.push(`${s.target === 'self' ? '' : 'ADV '}${STAT_ABBR[s.stat]} ${s.stages > 0 ? '▲'.repeat(Math.min(3, s.stages)) : '▼'.repeat(Math.min(3, -s.stages))}`)
  if (m.heal) t.push(`soin ${Math.round(m.heal * 100)}%`)
  if (m.wish) t.push(`vœu ${Math.round(m.wish * 100)}% ✨`)
  if (m.hits && m.hits > 1) t.push(`${m.hits}× coups`)
  if (m.priority) t.push(m.priority > 0 ? `priorité +${m.priority} ⏩` : `priorité ${m.priority}`)
  if (m.charge) t.push('charge 🔋')
  if (m.drain) t.push(`vol ${Math.round(m.drain * 100)}% 🩸`)
  if (m.recoil) t.push(`recul ${Math.round(m.recoil * 100)}% ⚠`)
  if (m.protect) t.push('protège 🛡')
  if (m.counter) t.push('riposte ↩')
  if (m.taunt) t.push('provoque 😡')
  if (m.substitute) t.push('substitut 👥')
  if (m.leech) t.push('parasite 🌱')
  if (m.setsWeather) t.push('météo 🌦')
  if (m.confuse) t.push(`confus ${Math.round(m.confuse * 100)}% 😵`)
  if (m.flinch) t.push(`flinch ${Math.round(m.flinch * 100)}%`)
  if (m.hazard) t.push('piège 🪤')
  if (m.hazardPoison) t.push('piège poison ☠️')
  if (m.hazardSlow) t.push('piège toile 🕸')
  if (m.clearHazard) t.push('balaye 🌀')
  if (m.accuracy !== undefined && m.accuracy < 0.97) t.push(`préc ${Math.round(m.accuracy * 100)}%`)
  if (m.effect) t.push(`${STATUS_INFO[m.effect].emoji} ${STATUS_INFO[m.effect].label} ${Math.round((m.effectChance ?? 0) * 100)}%`)
  return t
}

// ---------------------------------------------------------------------------
// Attaques apprises — la moveset curée par le joueur (local, localStorage).
//
// Contrainte d'architecture: les coups CHOISIS par le joueur ne sont PAS
// dérivables du seed, donc c'est de l'état local (comme le niveau et
// l'équipement). Ils sont encodés explicitement dans les replays et NE COMPTENT
// JAMAIS en arène classée (qui régénère les 2 coups de base depuis la chaîne).
//
// - max 4 coups; au-delà, apprendre un coup impose d'en remplacer un.
// - une nouvelle attaque est proposée à des paliers de niveau propres à la
//   famille (« suivant leur classe »); le coup proposé est déterministe
//   (dérivé de seed + niveau) donc l'offre est stable.
// ---------------------------------------------------------------------------

export const MAX_MOVES = 4

const KEY_MOVES = 'cryptoket.moves'
const KEY_LEARN = 'cryptoket.learn'

// Paliers d'apprentissage par famille — « suivant leur classe ». Les familles
// véloces apprennent tôt et souvent; les tanks plus tard.
export const LEARN_LEVELS: Record<Family, number[]> = {
  base: [8, 20, 38, 60, 84],
  meme: [10, 24, 42, 66, 88],
  ethereum: [12, 28, 46, 68, 90],
  defi: [14, 30, 50, 72, 92],
  stable: [16, 34, 55, 78, 95],
  bitcoin: [18, 38, 60, 82, 98],
}

// --- moveset apprise -------------------------------------------------------

function loadMoves(): Record<string, Move[]> {
  try {
    return JSON.parse(localStorage.getItem(KEY_MOVES) ?? '{}') as Record<string, Move[]>
  } catch {
    return {}
  }
}

function saveMoves(map: Record<string, Move[]>): void {
  localStorage.setItem(KEY_MOVES, JSON.stringify(map))
}

/** La moveset apprise d'un monstre, ou null s'il n'a jamais été personnalisé. */
export function learnedMovesOf(id: string): Move[] | null {
  const m = loadMoves()[id]
  return m && m.length > 0 ? m : null
}

/** Les 2 coups de base bruts (dérivés du seed) — point de départ d'une moveset. */
export function baseMovesOf(monster: Pick<Monster, 'family' | 'seed'>): Move[] {
  return generateMonster(monster.family, monster.seed).moves
}

/**
 * Remplace les coups du monstre par sa moveset apprise si elle existe. À appeler
 * comme applyGear (collection/fiche), JAMAIS en arène classée ni dans les
 * replays (qui portent leurs coups explicitement).
 */
export function applyLearnedMoves(monster: Monster): Monster {
  const learned = learnedMovesOf(monster.id)
  return learned ? { ...monster, moves: learned } : monster
}

function setLearned(id: string, moves: Move[]): void {
  const map = loadMoves()
  map[id] = moves.slice(0, MAX_MOVES)
  saveMoves(map)
}

// --- offres (accept / remplace / refuse) -----------------------------------

/** Une offre d'apprentissage: palier de famille, ou coup d'ÉVOLUTION (premium). */
export interface LearnOffer {
  level: number
  premium: boolean
  /** Clé de décision unique (les paliers famille et évolution ne collisionnent pas). */
  key: string
}

type Decision = 'a' | 'r' // accepté | refusé

function loadDecisions(): Record<string, Record<string, Decision>> {
  try {
    return JSON.parse(localStorage.getItem(KEY_LEARN) ?? '{}') as Record<
      string,
      Record<string, Decision>
    >
  } catch {
    return {}
  }
}

function saveDecision(id: string, key: string, d: Decision): void {
  const all = loadDecisions()
  const forId = all[id] ?? {}
  forId[key] = d
  all[id] = forId
  localStorage.setItem(KEY_LEARN, JSON.stringify(all))
}

/** Le coup proposé pour une offre (déterministe, stable). */
export function offeredMove(monster: Pick<Monster, 'family' | 'rarity' | 'seed'>, offer: LearnOffer): Move {
  return generateLearnableMove(monster.family, monster.rarity, monster.seed, offer.level, offer.premium)
}

/**
 * Offres en attente (niveau ≤ courant, non décidées): les paliers de la famille,
 * PLUS les coups d'évolution premium aux stades atteignables (plafonnés par le
 * potentiel d'évolution du mint).
 */
export function pendingLearnOffers(
  monster: Pick<Monster, 'id' | 'family' | 'seed' | 'rarity' | 'maxEvolutions'>,
  level: number,
): LearnOffer[] {
  const decided = loadDecisions()[monster.id] ?? {}
  const offers: LearnOffer[] = []
  for (const lv of LEARN_LEVELS[monster.family]) {
    if (lv <= level && !decided[String(lv)]) offers.push({ level: lv, premium: false, key: String(lv) })
  }
  // coups d'évolution: aux paliers de stade, dans la limite du potentiel du mint
  const evos = evolutionsOf(monster)
  for (const stage of [1, 2] as const) {
    const lv = STAGE_INFO[stage].minLevel
    const key = `e${lv}`
    if (stage <= evos && lv <= level && !decided[key]) offers.push({ level: lv, premium: true, key })
  }
  return offers.sort((a, b) => a.level - b.level)
}

/** La moveset ACTUELLE d'un monstre (apprise si elle existe, sinon les 2 de base). */
export function currentMovesetOf(monster: Monster): Move[] {
  return learnedMovesOf(monster.id) ?? baseMovesOf(monster)
}

/**
 * Accepte le coup d'une offre. Si la moveset est pleine (4), `replaceIndex`
 * indique le coup à écraser; sinon le coup s'ajoute. Retourne la nouvelle moveset.
 */
export function acceptOffer(monster: Monster, offer: LearnOffer, replaceIndex?: number): Move[] {
  const offered = offeredMove(monster, offer)
  const current = currentMovesetOf(monster)
  let next: Move[]
  if (current.length < MAX_MOVES && replaceIndex === undefined) {
    next = [...current, offered]
  } else {
    const idx = replaceIndex ?? current.length - 1
    next = current.map((mv, i) => (i === idx ? offered : mv))
  }
  setLearned(monster.id, next)
  saveDecision(monster.id, offer.key, 'a')
  return next
}

/** Refuse le coup d'une offre — l'offre est close, la moveset inchangée. */
export function refuseOffer(id: string, offer: LearnOffer): void {
  saveDecision(id, offer.key, 'r')
}

/** TUTEUR: enseigne directement un coup choisi (ajout ou remplacement). */
export function teachMove(monster: Monster, move: Move, replaceIndex?: number): Move[] {
  const current = currentMovesetOf(monster)
  let next: Move[]
  if (current.length < MAX_MOVES && replaceIndex === undefined) {
    next = [...current, { ...move, statChanges: move.statChanges?.map((s) => ({ ...s })) }]
  } else {
    const idx = replaceIndex ?? current.length - 1
    next = current.map((mv, i) => (i === idx ? { ...move, statChanges: move.statChanges?.map((s) => ({ ...s })) } : mv))
  }
  setLearned(monster.id, next)
  return next
}
